import { Component, AfterViewInit, OnChanges, OnDestroy, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { PlacePanel } from '@unpispas/upp-base';
import { ViewRenderer } from '@unpispas/upp-wdgt';

import { languageService } from '@unpispas/upp-base';
import { dataService, ProductOptView } from '@unpispas/upp-data';
import { i18nSupport } from '@unpispas/upp-data';
import { viewService } from '@unpispas/upp-base';
import { barcodeService } from '@unpispas/upp-base';

import { CatalogGrouped, GroupedInfo } from '@unpispas/upp-data';
import { PlaceView } from '@unpispas/upp-data';
import { TicketView } from '@unpispas/upp-data';
import { Product, ProductView } from '@unpispas/upp-data';
import { PreselectView } from '@unpispas/upp-data';
import { OfferView, MenuView } from '@unpispas/upp-data';
import { FamilyView } from '@unpispas/upp-data';

/************************************/
/* IN PANEL CATALOG BROWSER         */
/************************************/

type ParentInfo = {
    scroll: number,
    product: ProductView | null
}[]

/** Presentable row for the family-filter product grid. */
type FamilyGridItem = {
    product: ProductView;
    preselect: PreselectView | null;
}

class CatalogBrowser {
    constructor(
        private place: PlaceView | null,
        private change: ViewRenderer,
        private data: dataService
    ){
        // nothing to do
    }

    /*******************************/
    /* DISPLAY OPTIONS             */
    /*******************************/
    
    GroupTrack(index: number, group: GroupedInfo): any {
        return group.parent?._uuid || null;
    }

    ItemTrack(index: number, item: ProductView | PreselectView | OfferView | MenuView | FamilyView): any {
        return item._uuid;
    }

    FamilyItemTrack(index: number, item: FamilyGridItem): any {
        return item.preselect?._uuid || item.product?._uuid || null;
    }

    private _parent: ParentInfo = [ { scroll: 0, product: null } ];
    private _menu: OfferView | null = null;
    private _menugroup: MenuView | null = null;
    private _defaultFamily: FamilyView | null = null;

    get hasParent(): boolean {
        return (this._parent.length > 1) || (this._menu !== null);
    }

    get inFamily(): boolean {
        return (this._parent.length > 1);
    }

    get parent(): ProductView | null {
        return this._parent[0].product;
    }

    set parent(value: ProductView | null){
        this._parent[0].product = value;
    }

    get children(): number {
        if (this.parent){
            return this.parent.ProductsLength;
        }

        return this.place?.ProductsLength || 0;
    }

    get products(): Array <ProductView> {
        if (!this.place){
            return [];
        }

        return this.place.products;
    }

    get groups() : CatalogGrouped {
        if (!this.place){
            return [];
        }

        return this.place.GroupedProducts;
    }

    items(parent: ProductView | null): Array <ProductView> {
        const _groups = this.groups;
        for (const _group of _groups){
            if (_group.parent == parent){
                return _group.children;
            }
        }        
        return [];
    }

    get menus(): Array<OfferView> {
        if (!this.place){
            return [];
        }

        const _menus = this.place.offers.filter(_offer => _offer.IsMenu && _offer.IsAvailable && _offer.IsActive);
        _menus.sort((a, b) => {
            const _aname = a.name?.tr() || '';
            const _bname = b.name?.tr() || '';

            if (_aname < _bname) return -1;
            if (_aname > _bname) return 1;
            return 0;
        });

        return _menus;
    }

    get menu(): OfferView | null {
        return this._menu;
    }

    set menu(value: OfferView | null){
        this._menu = value;
        this._menugroup = null;
        this.change.markForCheck();
    }

    get menugroups(): Array<MenuView> {
        return this._menu?.groups || [];
    }

    get menugroup(): MenuView | null {
        return this._menugroup;
    }

    set menugroup(value: MenuView | null){
        this._menugroup = value;
        this.change.markForCheck();
    }

    /*******************************/
    /* FAMILY FILTER               */
    /*******************************/

    get families(): Array<FamilyView> {
        if (!this.place){
            return [];
        }

        const _families = this.place.families.filter(_family => _family.IsActive);
        _families.sort((a, b) => {
            const _asort = a.product?.sort ?? 0;
            const _bsort = b.product?.sort ?? 0;
            if (_asort === _bsort){
                const _aname = a.product?.name?.tr() || '';
                const _bname = b.product?.name?.tr() || '';
                if (_aname < _bname) return -1;
                if (_aname > _bname) return 1;
                return 0;
            }
            return _asort - _bsort;
        });

        return _families;
    }

    get ShowFamiliesTab(): boolean {
        return this.families.length > 0;
    }

    get defaultFamily(): FamilyView | null {
        return this._defaultFamily;
    }

    get ShowFamilyItems(): boolean {
        return (this._defaultFamily !== null) && !this.inFamily;
    }

    IsDefaultFamily(family: FamilyView | null): boolean {
        return family === this._defaultFamily;
    }

    /**
     * Sets the active family filter. Changing family clears menu/group/parent
     * navigation (same as MoveHome). Pass null for "view all".
     */
    SetDefaultFamily(family: FamilyView | null): void {
        if (this._defaultFamily !== family){
            this._defaultFamily = family;
            this.MoveHome(null);
        }
        this.change.markForCheck();
    }

    /**
     * Sellable rows for a family: product and/or preselect views, in `sort` order.
     * Invalid FAMILYPRODUCT rows are already omitted by `FamilyView.products`.
     */
    FamilyItems(family: FamilyView | null = this._defaultFamily): Array<FamilyGridItem> {
        if (!family){
            return [];
        }

        const _items: Array<FamilyGridItem> = [];
        for (const _row of family.products){
            if (_row.product){
                const _product = this.data.alives.get(_row.product) as ProductView | null;
                if (_product){
                    _items.push({ product: _product, preselect: null });
                }
            }

            if (_row.preselect){
                const _preselect = this.data.alives.get(_row.preselect) as PreselectView | null;
                if (_preselect?.product){
                    _items.push({ product: _preselect.product, preselect: _preselect });
                }
            }
        }

        return _items;
    }

    /*******************************/
    /* EVENT HANDLERS              */
    /*******************************/  
    
    OnMenuItem(offer: OfferView){
        this.menu = offer;
    }

    OnMenuGroup(group: MenuView){
        this.menugroup = group;
    }

    SetParent(product: ProductView, element: ElementRef | null){
        if (this.parent == product){
            return;     // this is the same parent: ignore
        }

        let scrlpos = 0;
        
        const scrollable = element?.nativeElement.closest('.upp-scrollable-content') || null;
        if (scrollable){
            scrlpos = scrollable.scrollTop;
            setTimeout(() => {
                scrollable.scrollTop = 0;
            }, 0);
        }

        const _next = {
            scroll: scrlpos,
            product: product
        };

        this._parent.unshift(_next);
        this.change.markForCheck();
    }

    MoveHome(element: ElementRef | null){
        const scrollable = element?.nativeElement.closest('.upp-scrollable-content') || null;
        if (scrollable){
            setTimeout(() => {
                scrollable.scrollTop = 0;
            }, 0);
        }

        this._parent = [ { scroll: 0, product: null } ];
        this._menu = null;
        this._menugroup = null;
        this.change.markForCheck();
    }

    MoveBack(element: ElementRef | null){
        if (this.inFamily){
            const _prev = this._parent.shift();
            if (_prev){
                const scrollable = element?.nativeElement.closest('.upp-scrollable-content') || null;
                if (scrollable){
                    setTimeout(() => {
                        scrollable.scrollTop = _prev.scroll;
                    }, 0);
                }
            }

            this.change.markForCheck();
            return;
        }

        if (this._menugroup){
            this._menugroup = null;
            this.change.markForCheck();
            return;
        }

        if (this._menu){
            this._menu = null;
            this.change.markForCheck();
        }
    }
}

/************************************/
/* IN PANEL PRODUCTS SEARCH         */
/************************************/

class ProductsSearch {
    private _delay_timeout: ReturnType<typeof setTimeout> | null = null;
    private _search_result: Array <ProductView> = [];

    constructor(private place: PlaceView | null, private change: ViewRenderer){
        // nothing to do
    }

    /*******************************/
    /* DISPLAY OPTIONS             */
    /*******************************/

    private get MinLength(): number {
        return 3;
    }

    private _criteria = '';
    get criteria(){
        return this._criteria;
    }

    set criteria(value: string){
        this._criteria = value;

        if (this._delay_timeout){
            clearTimeout(this._delay_timeout);
            this._delay_timeout = null;
        }

        if (this._criteria.length > this.MinLength){
            this._delay_timeout = setTimeout(() => {
                this.doSearch(this._criteria);
            }, 500);    
        }
    }

    get results(){
        return this._search_result;
    }

    get Searching(): boolean {
        return (!!this._criteria) && (this.criteria.length > this.MinLength);
    }

    /*******************************/
    /* EVENT HANDLERS              */
    /*******************************/  

    Clear(){
        this._criteria = '';
    }

    /*******************************/
    /* PRIVATE METHODS             */
    /*******************************/  

    private doSearch(criteria: string){
        if (!this.place){
            return;
        }

        this._search_result = [];

        const _results: Array <ProductView | PreselectView> = this.place.doSearch(criteria);
        for (const _result of _results){
            if (_result.ViewOf(Product)){   // filter only product results
                this._search_result.push(_result as ProductView);
            }
        }

        this.change.markForCheck();
    }
}

/************************************/
/* SELECT TAB COMPONENT             */
/************************************/

@Component({
    selector: 'upp-cart-select',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-select.html',
    styleUrl: './upp-select.scss',
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef]
    }]
})
export class UppCartSelectComponent extends i18nSupport implements AfterViewInit, OnChanges, OnDestroy {
    @Input() place: PlaceView | null = null;
    @Input() ticket: TicketView | null = null;
    @Input() locked = false;

    @Output() ToPanel = new EventEmitter<PlacePanel>();

    private _place_subscription: Subscription | null = null;

    constructor(
        private lang: languageService,
        private change: ViewRenderer,
        private data: dataService,
        public view: viewService,
        private barcode: barcodeService
    ){
        super(lang, change.cdref);
    }
    
    ngAfterViewInit(): void {
        this.change.markForCheck();
    }

    ngOnChanges(changes?: SimpleChanges){
        const _changes = changes || {};
        if (_changes['locked'] && !_changes['place'] && !_changes['ticket']){
            this.StartScan();
            this.change.markForCheck();
            return;
        }

        this._catalogbrowser = null;
        this._productsearch = null;

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        if (this.place) {
            this._place_subscription = this.place.OnViewRefresh.subscribe(
            () => {
                this.StartScan();
            });
        }

        this.StartScan();
    }

    ngOnDestroy(): void {
        super.OnDestroy();

        if (this._place_subscription){
            this._place_subscription.unsubscribe();
            this._place_subscription = null;
        }

        this.StopScan();
    }

    /*******************************/
    /* CATALOG OPERATIONS          */
    /*******************************/

    private _catalogbrowser: CatalogBrowser | null = null;
    get browser(): CatalogBrowser {
        if (!this._catalogbrowser){
            this._catalogbrowser = new CatalogBrowser(this.place, this.change, this.data);
        }
        return this._catalogbrowser;
    }

    private _productsearch: ProductsSearch | null = null;
    get search(): ProductsSearch {
        if (!this._productsearch){
            this._productsearch = new ProductsSearch(this.place, this.change);
        }
        return this._productsearch;
    }

    /*******************************/
    /* DISPLAY OPTIONS             */
    /*******************************/

    @ViewChild ('scrollable', { read: ElementRef }) _scrollable: ElementRef | null = null;

    ToProductsGrid(product: ProductView) : boolean {
        if (product.IsFamily){
            return false;   // do not show families in the grid
        }

        if (product.IsCatalog){
            return false;   // do not show catalog products
        }

        if (product.IsGroup && product.IsEmpty){
            return false;   // do not show empty groups
        }

        return true;    // TODO: añadir control de carga con delayed
    }

    ToSelectsGrid(product: ProductView) : boolean {
        if (!product.IsProduct){
            return false;   // only show products
        }

        if (product.IsCatalog){
            return false;   // do not show catalog products
        }

        if (product.IsEmpty){
            return false;   // do not show empty products
        }

        return true;    // TODO: añadir control de carga con delayed
    }

    /*******************************/
    /* FAMILY FILTER UI            */
    /*******************************/

    private _showFamilyOptions = false;

    get ShowFamilyOptions(): boolean {
        return this._showFamilyOptions;
    }

    /** Toggle mobile family dropdown; stopPropagation so overlay taps do not bubble. */
    OnFamilyOptions(event: Event | null = null): void {
        if (event){
            event.stopPropagation();
            event.preventDefault();
        }
        this._showFamilyOptions = !this._showFamilyOptions;
        this.change.markForCheck();
    }

    /** Select family filter (or null = view all) and close the mobile options panel. */
    OnSetDefaultFamily(family: FamilyView | null): void {
        this.browser.SetDefaultFamily(family);
        this._showFamilyOptions = false;
        this.change.markForCheck();
    }

    /** Translated family product name for labels and stable `uppDataid` values. */
    FamilyLabel(family: FamilyView): string {
        const _name = family.product?.name ?? null;
        return this.tr(_name) || '';
    }

    /** Product photo URL for the family icon, or null when none. */
    FamilyPhoto(family: FamilyView): string | null {
        return family.product?.photo || null;
    }

    /*******************************/
    /* EVENT HANDLERS              */
    /*******************************/  
    
    /**
     * Opens the ticket panel. Bound both from the desktop/mobile auto-show flow
     * in {@link OnSelect} and from explicit mobile taps (summary bar and header
     * forward button) in the template.
     * @param force Bypasses the desktop-only gate; explicit user actions (mobile
     * summary bar / header forward arrow) pass `true` so the panel opens regardless
     * of platform. Without it, the panel only opens on desktop, keeping the catalog
     * full-screen on mobile after a direct add-to-cart.
     */
    async ShowTicket(force = false){
        if (!force && !this.view.Desktop){
            return;
        }

        this.ToPanel.emit({
            scnd: 'ticket',
            args: {
                ticket: this.ticket
            },
            force: true
        });
    }

    /**
     * Mobile footer action buttons (send/cash/card): open the ticket panel with a
     * one-shot `action` so `upp-ticket-edit` runs the same commit/payment handlers
     * as its own buttons, without duplicating that orchestration here.
     */
    OnFooter(event: Event, action: 'send' | 'cash' | 'card'): void {
        event.stopPropagation();

        if (this.locked || !this.ticket){
            return;
        }

        this.ToPanel.emit({
            scnd: 'ticket',
            args: {
                ticket: this.ticket,
                action: action
            },
            force: true
        });
    }

    /**
     * Opens the product-options configurator for the in-progress cart ticket.
     * Marks `force` so the cart leave-guard does not treat this as abandoning CART.
     */
    private async ShowProduct(product: ProductView | null, options: Array <ProductOptView>){
        this.ToPanel.emit({
            scnd: 'product',
            args: {
                ticket: this.ticket,
                product: product,
                options: options
            },
            force: true
        });
    }

    OnSelect(product: ProductView | PreselectView | null, tocart: boolean){
        if (this.locked || !this.ticket || !product){
            return;
        }

        if (!product.IsAvailable){
            return;
        }
        
        // product selected
        if (product.ViewOf(Product)){
            const _productview = product as ProductView;
            if (_productview.IsGroup || (!tocart && (_productview.selects.length > 0))){
                this.browser.SetParent(_productview, this._scrollable);
            }
            else {
                // can be added to cart without configuration
                if (_productview.IsAbleToCart){
                    this.ShowTicket().then(() => {
                        if (this.ticket){
                            this.ticket.ToCart(_productview, [], 1, null);
                        }
                    });
                }

                // requires product options configuration
                else {
                    this.ShowProduct(_productview, []);
                }
            }
        }

        // preselect selected
        else {
            const _preselectview = product as PreselectView;
            if (_preselectview.product){
                if (_preselectview.IsAbleToCart){
                    this.ShowTicket().then(() => {
                        if (this.ticket && _preselectview.product){
                            this.ticket.ToCart(_preselectview.product, _preselectview.options, 1, null);
                        }
                    });
                }
                else {
                    this.ShowProduct(_preselectview.product, _preselectview.options);
                }
            }
            else {
                console.warn("[TOCART] This shortcut hos no related product!");
            }
        }
    }

    MoveHome(){
        this.browser.MoveHome(this._scrollable);
    }

    MoveBack(){
        this.browser.MoveBack(this._scrollable);
    }

    /***************************************/
    /* PRODUCT SEARCH                      */
    /***************************************/

    private _waitMatchPanel = false;
    private _matchResolve: ((result: ProductView | PreselectView | null) => void) | null = null;

    private _matchPanelOpen = false;

    get ShowMatchPanel(): boolean {
        return this._matchPanelOpen;
    }

    set ShowMatchPanel(value: boolean) {
        if (this._matchPanelOpen === value) {
            return;
        }

        this._matchPanelOpen = value;
        this.change.markForCheck();
    }

    MatchBarcode = '';

    async OnProductCodeSearch(): Promise<void> {
        if (this.locked || (!this.place?.ProductCodes && !this.place?.HasCodeScan)){
            return;
        }

        const _result = await this._ShowMatchPanel();
        if (_result){
            this.OnSelect(_result, true);
        }
    }

    private _ShowMatchPanel(barcode = ''): Promise<ProductView | PreselectView | null> {
        if (this._waitMatchPanel || !this.place){
            return Promise.resolve(null);
        }

        this._waitMatchPanel = true;
        return new Promise((resolve) => {
            this._matchResolve = resolve;
            this.MatchBarcode = barcode;
            this.ShowMatchPanel = true;
        });
    }

    OnMatchPanel(result: ProductView | PreselectView | null): void {
        this.ShowMatchPanel = false;
        this._matchResolve?.(result);
        this._matchResolve = null;
        setTimeout(() => {
            this._waitMatchPanel = false;
        }, 100);
    }

    /*******************************/
    /* BARCODE SCANNER (HID)       */
    /*******************************/

    private StartScan(): void {
        if (this.locked){
            this.StopScan();
            this.change.markForCheck();
            return;
        }

        if (this.place?.HasCodeScan ?? false){
            this.barcode.enable((code: string) => this.OnScanned(code), { mode: 'cart' });
        }
        else {
            this.StopScan();
        }

        this.change.markForCheck();
    }

    private StopScan(): void {
        this.barcode.disable();
    }

    private async OnScanned(code: string): Promise<void> {
        if (this.locked || !this.place || !code){
            return;
        }

        const _matches = this.place.doBarCode(code);
        if (_matches.length === 0){
            return;
        }

        if (_matches.length > 1){
            this.search.Clear();
            const _result = await this._ShowMatchPanel(code);
            if (_result){
                this.OnSelect(_result, true);
            }
            return;
        }

        this.OnSelect(_matches[0], true);
    }
}